/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC } from "react";
import { useTranslation } from "react-i18next";

import styles from "./CallSubtitle.module.css";
import { ParticipantPill } from "../components/ParticipantPill";
import { useBehavior } from "../useBehavior";
import { type WrappedUserMediaViewModel } from "../state/media/WrappedUserMediaViewModel";

interface ParticipantPillItemProps {
  vm: WrappedUserMediaViewModel;
}

const ParticipantPillItem: FC<ParticipantPillItemProps> = ({ vm }) => {
  const name = useBehavior(vm.displayName$);
  const speaking = useBehavior(vm.speaking$);
  return <ParticipantPill name={name} speaking={speaking} />;
};

export type CallSubtitleVariant = "audio_1to1" | "audio_group" | "video_1to1";

interface Props {
  /** The other participants in the call (excludes the local user). */
  participants: WrappedUserMediaViewModel[];
  variant: CallSubtitleVariant;
}

/**
 * The "in <call type> with X" row shown under the room name, matching the
 * Figma call-page design. Hidden for group video calls (the grid layout),
 * which have no equivalent row.
 */
export const CallSubtitle: FC<Props> = ({ participants, variant }) => {
  const { t } = useTranslation();
  if (participants.length === 0) return null;

  const label =
    variant === "audio_1to1"
      ? t("call_subtitle.in_audio_call_with")
      : variant === "audio_group"
        ? t("call_subtitle.in_group_audio_call_with")
        : t("call_subtitle.in_video_call_with");

  return (
    <div className={styles.subtitle}>
      <p className={styles.label}>{label}</p>
      <div className={styles.pills}>
        {participants.map((p) => (
          <ParticipantPillItem key={p.id} vm={p} />
        ))}
      </div>
    </div>
  );
};
